import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Paper,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ToggleOnOutlinedIcon from "@mui/icons-material/ToggleOnOutlined";
import ToggleOffOutlinedIcon from "@mui/icons-material/ToggleOffOutlined";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../../redux/store";
import { ROUTES } from "../../router/constants";
import { adminUpdateUser, updateUserIsActive, usersList } from "../../services/usersList.service";
import { toast } from "sonner";
import dayjs from "../../lib/dayjs";

interface UserWithMeta {
  id: string;
  name: string;
  email: string;
  role: string;
  created_at?: string;
  isActive?: boolean;
}

const ROLE_LABELS: Record<string, string> = {
  USER: "Usuário",
  ADMIN: "Administrador",
  OWNER: "Proprietário",
  SECRETARY: "Secretaria",
  FINANCIAL: "Financeiro",
  DOCTOR: "Médico",
  DOCTOR_OWNER: "Médico proprietário",
};

function getRoleLabel(role: string): string {
  const upper = String(role || "").toUpperCase();
  return ROLE_LABELS[upper] ?? upper;
}

export function UsersList() {
  const navigate = useNavigate();
  const { user: loggedUser } = useSelector((state: RootState) => state.userInfo);
  const rawUsers = useSelector((state: RootState) => state.userList) as UserWithMeta[];

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [nameFilter, setNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("active");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [createdFrom, setCreatedFrom] = useState<string>(""); // yyyy-mm-dd
  const [createdTo, setCreatedTo] = useState<string>("");   // yyyy-mm-dd

  // Modal de edição (ADMIN)
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editingUser, setEditingUser] = useState<UserWithMeta | null>(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editRole, setEditRole] = useState<string>("USER");
  const [editIsActive, setEditIsActive] = useState(true);
  const [editPassword, setEditPassword] = useState("");
  const [editConfirmPassword, setEditConfirmPassword] = useState("");

  // Guard de autorização: apenas ADMIN, OWNER e DOCTOR_OWNER podem ver esta tela
  const allowedRoles = ["ADMIN", "OWNER", "DOCTOR_OWNER"] as const;
  const loggedRoleUpper = String(loggedUser?.role || "").toUpperCase();
  const isAllowed = allowedRoles.includes(loggedRoleUpper as (typeof allowedRoles)[number]);

  useEffect(() => {
    if (!isAllowed) {
      // Se for usuário comum, manda para a home; caso contrário, dashboard
      const isUserRole = loggedRoleUpper === "USER";
      navigate(isUserRole ? ROUTES.HOME : ROUTES.DASHBOARD, { replace: true });
      return;
    }
  }, [isAllowed, loggedRoleUpper, navigate]);

  useEffect(() => {
    if (!isAllowed) return;

    let ignore = false;

    async function load() {
      try {
        setLoading(true);
        setError(null);
        await usersList();
      } catch (e) {
        if (!ignore) {
          setError("Não foi possível carregar a lista de usuários. Tente novamente.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      ignore = true;
    };
  }, [isAllowed]);

  const users: UserWithMeta[] = useMemo(() => {
    return (rawUsers || []).map((u) => ({
      ...u,
      role: u.role,
      created_at: u.created_at,
      // Se o backend não enviar isActive, consideramos true para compatibilidade
      isActive: typeof u.isActive === "boolean" ? u.isActive : true,
    }));
  }, [rawUsers]);

  const availableRoles = useMemo(() => {
    const set = new Set<string>();
    users.forEach((u) => {
      if (u.role) {
        set.add(String(u.role).toUpperCase());
      }
    });
    return Array.from(set).sort();
  }, [users]);

  const filteredUsers = useMemo(() => {
    const nameFilterLower = nameFilter.trim().toLowerCase();

    return users.filter((u) => {
      const isActive = u.isActive ?? true;

      const matchesName = !nameFilterLower
        ? true
        : u.name.toLowerCase().includes(nameFilterLower) ||
          u.email.toLowerCase().includes(nameFilterLower);

      const matchesStatus =
        statusFilter === "all"
          ? true
          : statusFilter === "active"
          ? isActive
          : !isActive;

      const roleUpper = String(u.role || "").toUpperCase();
      const matchesRole = roleFilter === "all" ? true : roleUpper === roleFilter;

      let matchesDate = true;
      if (createdFrom && u.created_at) {
        matchesDate = matchesDate && dayjs(u.created_at).isSameOrAfter(createdFrom, "day");
      }
      if (createdTo && u.created_at) {
        matchesDate = matchesDate && dayjs(u.created_at).isSameOrBefore(createdTo, "day");
      }

      return matchesName && matchesStatus && matchesRole && matchesDate;
    })
    // Ordena por data de criação (mais recente primeiro) e, em seguida, por nome
    .sort((a, b) => {
      const aDate = a.created_at ? dayjs(a.created_at).valueOf() : 0;
      const bDate = b.created_at ? dayjs(b.created_at).valueOf() : 0;

      if (aDate !== bDate) return bDate - aDate;
      return a.name.localeCompare(b.name);
    });
  }, [users, nameFilter, statusFilter, roleFilter, createdFrom, createdTo]);

  const handleGoToUser = (id: string) => {
    navigate(ROUTES.USERINFO.replace(":id", id));
  };

  const handleToggleUserActive = async (user: UserWithMeta) => {
    const current = typeof user.isActive === "boolean" ? user.isActive : true;
    await updateUserIsActive(user.id, !current);
  };

  const canAdminEdit = loggedRoleUpper === "ADMIN";

  const handleOpenEdit = (user: UserWithMeta) => {
    setEditingUser(user);
    setEditName(user.name ?? "");
    setEditEmail(user.email ?? "");
    setEditRole(String(user.role || "USER").toUpperCase());
    setEditIsActive(typeof user.isActive === "boolean" ? user.isActive : true);
    setEditPassword("");
    setEditConfirmPassword("");
    setEditOpen(true);
  };

  const handleCloseEdit = () => {
    if (editLoading) return;
    setEditOpen(false);
    setEditingUser(null);
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;

    if (!editName.trim() || !editEmail.trim()) {
      toast.error("Nome e e-mail são obrigatórios.");
      return;
    }

    if ((editPassword || editConfirmPassword) && editPassword !== editConfirmPassword) {
      toast.error("Senha e confirmação não conferem.");
      return;
    }

    try {
      setEditLoading(true);
      await adminUpdateUser(editingUser.id, {
        name: editName.trim(),
        email: editEmail.trim(),
        role: editRole,
        isActive: editIsActive,
        ...(editPassword ? { password: editPassword, confirmPassword: editConfirmPassword } : {}),
      });
      toast.success("Usuário atualizado com sucesso.");
      handleCloseEdit();
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <Box
      width="100%"
      padding={4}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1000, mx: "auto", mt: 6 }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Verificar usuários
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Liste e filtre os usuários ativos por nome, status, função (role) e data de criação.
        </Typography>

        <Card sx={{ mt: 2, mb: 3 }}>
          <CardContent>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Buscar por nome ou e-mail"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="active">Ativos</MenuItem>
                  <MenuItem value="inactive">Inativos</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="role-filter-label">Tipo de perfil</InputLabel>
                <Select
                  labelId="role-filter-label"
                  label="Tipo de perfil"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value as string)}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  {availableRoles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {getRoleLabel(role)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              sx={{ mt: 2 }}
            >
              <TextField
                label="Criado a partir de"
                type="date"
                value={createdFrom}
                onChange={(e) => setCreatedFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Criado até"
                type="date"
                value={createdTo}
                onChange={(e) => setCreatedTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
          </CardContent>
        </Card>

        {loading && (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Typography color="error" mt={2}>
            {error}
          </Typography>
        )}

        {!loading && !error && (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Função (role)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Data de criação</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((u) => {
                  const createdLabel = u.created_at
                    ? dayjs(u.created_at).format("DD/MM/YYYY HH:mm")
                    : "-";
                  const isActive = u.isActive ?? true;

                  return (
                    <TableRow key={u.id} hover>
                      <TableCell>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => handleGoToUser(u.id)}
                        >
                          {u.name}
                        </Button>
                      </TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{getRoleLabel(u.role)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={isActive ? "Ativo" : "Inativo"}
                          color={isActive ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell>{createdLabel}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditOutlinedIcon />}
                            onClick={() => {
                              if (canAdminEdit) {
                                handleOpenEdit(u);
                              } else {
                                handleGoToUser(u.id);
                              }
                            }}
                          >
                            Editar
                          </Button>

                          <Button
                            size="small"
                            variant={isActive ? "outlined" : "contained"}
                            color={isActive ? "error" : "success"}
                            startIcon={
                              isActive ? <ToggleOffOutlinedIcon /> : <ToggleOnOutlinedIcon />
                            }
                            onClick={() => handleToggleUserActive(u)}
                          >
                            {isActive ? "Desativar" : "Ativar"}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {filteredUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nenhum usuário encontrado com os filtros atuais.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Dialog open={editOpen} onClose={handleCloseEdit} fullWidth maxWidth="sm">
        <DialogTitle>Editar usuário</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nome"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              fullWidth
            />

            <TextField
              label="E-mail"
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
              fullWidth
            />

            <FormControl fullWidth size="small">
              <InputLabel id="edit-role-label">Role</InputLabel>
              <Select
                labelId="edit-role-label"
                label="Role"
                value={editRole}
                onChange={(e) => setEditRole(String(e.target.value))}
              >
                {Object.keys(ROLE_LABELS).map((role) => (
                  <MenuItem key={role} value={role}>
                    {getRoleLabel(role)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel id="edit-active-label">Status</InputLabel>
              <Select
                labelId="edit-active-label"
                label="Status"
                value={editIsActive ? "active" : "inactive"}
                onChange={(e) => setEditIsActive(e.target.value === "active")}
              >
                <MenuItem value="active">Ativo</MenuItem>
                <MenuItem value="inactive">Inativo</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              Redefinir senha (opcional)
            </Typography>

            <TextField
              label="Nova senha"
              type="password"
              value={editPassword}
              onChange={(e) => setEditPassword(e.target.value)}
              fullWidth
            />

            <TextField
              label="Confirmar nova senha"
              type="password"
              value={editConfirmPassword}
              onChange={(e) => setEditConfirmPassword(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} color="inherit" disabled={editLoading}>
            Cancelar
          </Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={editLoading}>
            {editLoading ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
